// Pre-match hero picker: portrait-first cards grouped by class, mouse/keyboard selectable, confirms into
// a callback. Holds no assumption about starting a local match — main.ts owns that — so stage 2b's network
// lobby can reuse it verbatim, feeding `taken` as teammates claim heroes and treating confirm as "ready".
import { HEROES, CLASSES, type HeroKind, type ClassId } from '../core/heroes.js';
import { SKILLS } from '../core/skills.js';
import { skillDesc, passiveInfo } from './heroInfo.js';
import { heroIcons, iconSvg, iconLabel } from './heroIcons.js';
import { t } from './i18n/index.js';
import { className, heroName, passiveName, roleName, skillName } from './i18n/names.js';
import { escapeHtml } from './nameStore.js';

const CLASS_ORDER = Object.keys(CLASSES) as ClassId[];
const GRID: HeroKind[][] = CLASS_ORDER.map(cls => CLASSES[cls].chars as HeroKind[]);  // [col][row], col = class
/* Reading order (top-to-bottom within a class, class left-to-right) — also HEROES' own key order. */
export const HERO_ORDER: HeroKind[] = GRID.flat();

function findPos(id: HeroKind): [number, number] {
  for (let c = 0; c < GRID.length; c++) {
    const r = GRID[c]!.indexOf(id);
    if (r !== -1) return [c, r];
  }
  return [0, 0];
}

type ArrowKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown';
const STEP: Record<ArrowKey, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

/* Grid arrow-move: clamps at the edges (returns `current` unchanged) and skips over disabled/taken cells
   rather than landing on them, scanning further in the same direction. DOM-free by design. */
export function moveSelection(current: HeroKind, key: ArrowKey, disabled: ReadonlySet<HeroKind> = new Set()): HeroKind {
  let [c, r] = findPos(current);
  const [dc, dr] = STEP[key];
  for (;;) {
    c += dc; r += dr;
    const col = GRID[c];
    if (!col || r < 0 || r >= col.length) return current;
    const cand = col[r]!;
    if (!disabled.has(cand)) return cand;
  }
}

/* `initial` if given and free, else the first free hero in reading order (HERO_ORDER[0] if all are taken). */
export function firstAvailable(disabled: ReadonlySet<HeroKind>, initial?: HeroKind): HeroKind {
  if (initial && !disabled.has(initial)) return initial;
  return HERO_ORDER.find(k => !disabled.has(k)) ?? HERO_ORDER[0]!;
}

export type HeroSelectOptions = {
  taken?: Partial<Record<HeroKind, string>>;  // heroId -> label of who holds it (teammate name); disables the card
  initial?: HeroKind;
  confirmLabel?: string;                      // default «В бой» (hs.confirm)
  title?: string;                             // default «Выбор героя» (hs.title)
  onConfirm?: (hero: HeroKind) => void;       // omitted: browse only, no confirm button
  onBack?: () => void;                        // «Назад» button + Esc
  mapPicker?: MapPicker;                      // a bot match: which map to play
};

export type MapPicker = { choices: { id: string; label: string; group: string }[]; value: string; onChange(id: string): void };

function mapPickerHtml(p: MapPicker): string {
  const groups = [...new Set(p.choices.map(c => c.group))];
  return `<label class="hs-map">${t('hs.map')} <select id="hsMap">${groups.map(g => `<optgroup label="${escapeHtml(g)}">${p.choices.filter(c => c.group === g).map(c =>
    `<option value="${escapeHtml(c.id)}"${c.id === p.value ? ' selected' : ''}>${escapeHtml(c.label)}</option>`).join('')}</optgroup>`).join('')}</select></label>`;
}

export type HeroSelectHandle = {
  destroy(): void;
  /** Stage 2b: refresh as teammates pick, reselecting away if the current pick was just taken. */
  setTaken(taken: Partial<Record<HeroKind, string>>): void;
};

function cardHtml(id: HeroKind): string {
  return `<button type="button" class="hs-card" data-hero="${id}">
    <img class="hs-portrait" src="portraits/${id}.jpg" alt="" width="480" height="640" loading="lazy">
    <span class="hs-name">${heroName(id)}</span>
    <span class="hs-marks">${heroIcons(id).map(i => iconSvg(i)).join('')}</span>
    <span class="hs-taken"></span>
  </button>`;
}

function statRow(label: string, value: string): string {
  return `<div class="hs-stat"><span>${label}</span><b>${value}</b></div>`;
}

function skillHtml(key: 'Q' | 'W', id: HeroKind): string {
  const sk = (SKILLS as Record<string, any>)[id][key];
  return `<div class="hs-skill"><b>${key}</b><div><span class="hs-skill-name">${skillName(id, key)}</span> <span class="hs-skill-cd">${t('common.cooldown', { cd: sk.cd })}</span><p>${skillDesc(id, key)}</p></div></div>`;
}

function passiveHtml(id: HeroKind): string {
  return `<div class="hs-skill hs-passive"><b>P</b><div><p>${t('common.passiveLine', { name: passiveName(id), desc: passiveInfo(id) })}</p></div></div>`;
}

export function renderHeroSelect(root: HTMLElement, opts: HeroSelectOptions): HeroSelectHandle {
  let taken: Partial<Record<HeroKind, string>> = opts.taken ?? {};
  let selected = firstAvailable(new Set(Object.keys(taken) as HeroKind[]), opts.initial);

  root.innerHTML = `
    <div class="hs-plate">${opts.onBack ? `<button type="button" class="hs-back" id="hsBack">${t('common.back')}</button>` : ''}<h1>${opts.title ?? t('hs.title')}</h1>${opts.mapPicker ? mapPickerHtml(opts.mapPicker) : ''}</div>
    <div class="hs-body">
      <div class="hs-classes">${CLASS_ORDER.map(cls => `
        <div class="hs-class">
          <h2>${className(cls)}</h2>
          ${CLASSES[cls].chars.map(id => cardHtml(id as HeroKind)).join('')}
        </div>`).join('')}</div>
      <div class="hs-detail" id="hsDetail"></div>
    </div>`;

  const cards = new Map<HeroKind, HTMLButtonElement>();
  root.querySelectorAll<HTMLButtonElement>('.hs-card').forEach(el => cards.set(el.dataset.hero as HeroKind, el));
  const detail = root.querySelector('#hsDetail') as HTMLElement;

  function renderDetail() {
    const def = HEROES[selected];
    detail.innerHTML = `
      <div class="hs-hero">
        <h2>${heroName(selected)}</h2>
        <p class="hs-role">${className(def.cls)} · ${roleName(def.role)}</p>
        <ul class="hs-traits">${heroIcons(selected).map(i => `<li>${iconSvg(i)}${iconLabel(i)}</li>`).join('')}</ul>
      </div>
      <div class="hs-stats">
        ${statRow(t('hs.hp'), String(def.hp))}
        ${statRow(t('hs.speed'), def.speed.toFixed(1))}
        ${statRow(t('hs.range'), def.range.toFixed(1))}
        ${statRow(t('hs.dmg'), String(def.dmg))}
      </div>
      <div class="hs-skills">
        ${skillHtml('Q', selected)}
        ${skillHtml('W', selected)}
        ${passiveHtml(selected)}
      </div>
      ${opts.onConfirm ? `<button type="button" class="hs-confirm" id="hsConfirm"${taken[selected] ? ' disabled' : ''}>${opts.confirmLabel ?? t('hs.confirm')}</button>` : ''}`;
    detail.querySelector('#hsConfirm')?.addEventListener('click', () => opts.onConfirm?.(selected));
  }

  function renderTaken() {
    for (const [id, el] of cards) {
      const label = taken[id];
      el.disabled = !!label;
      el.classList.toggle('taken', !!label);
      el.classList.toggle('on', id === selected);
      (el.querySelector('.hs-taken') as HTMLElement).textContent = label ?? '';
    }
  }

  /* Only updates state + re-renders; DOM focus (and the 'focus' listener below that calls this back in)
     is driven separately by click/Tab/arrow handling, so this never fights the browser's own focus. */
  function applySelection(id: HeroKind) {
    if (taken[id] || id === selected) return;
    cards.get(selected)?.classList.remove('on');
    selected = id;
    cards.get(selected)?.classList.add('on');
    renderDetail();
  }

  for (const [id, el] of cards) el.addEventListener('focus', () => applySelection(id));

  function onKeydown(e: KeyboardEvent) {
    if ((e.target as HTMLElement).tagName === 'SELECT') return;  // the map picker's own arrows and Enter
    if (e.code === 'ArrowLeft' || e.code === 'ArrowRight' || e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      e.preventDefault();
      const next = moveSelection(selected, e.code, new Set(Object.keys(taken) as HeroKind[]));
      cards.get(next)?.focus();
    } else if (e.code === 'Enter' && !e.repeat && !taken[selected] && opts.onConfirm && (e.target as HTMLElement).id !== 'hsBack') {
      opts.onConfirm(selected);
    }
  }
  root.addEventListener('keydown', onKeydown);
  // On window, not root: Esc must work wherever focus is while the picker shows (e.g. after a click on
  // the detail panel). defaultPrevented: someone nearer the target already handled this Esc.
  function onEscape(e: KeyboardEvent) {
    if (e.key !== 'Escape' || e.defaultPrevented || !opts.onBack) return;
    e.preventDefault();
    opts.onBack();
  }
  if (opts.onBack) window.addEventListener('keydown', onEscape);
  if (opts.onBack) root.querySelector('#hsBack')!.addEventListener('click', opts.onBack);
  const mapSelect = root.querySelector('#hsMap') as HTMLSelectElement | null;
  mapSelect?.addEventListener('change', () => opts.mapPicker!.onChange(mapSelect.value));

  renderTaken();
  renderDetail();
  cards.get(selected)?.focus({ preventScroll: true });

  return {
    destroy() {
      root.removeEventListener('keydown', onKeydown);
      if (opts.onBack) window.removeEventListener('keydown', onEscape);
      root.innerHTML = '';
    },
    setTaken(next) {
      taken = next;
      renderTaken();
      if (taken[selected]) {
        selected = firstAvailable(new Set(Object.keys(taken) as HeroKind[]));
        renderTaken();
        renderDetail();
      }
    },
  };
}
